import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, update, onValue } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { Heart, BookOpen, ArrowLeft, Search } from "lucide-react";

export default function Saved() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    // Real-time: re-runs whenever saved list OR any listing changes
    const unsub = onValue(ref(db, `saved/${currentUser.uid}`), async (savedSnap) => {
      if (!savedSnap.exists()) { setListings([]); setLoading(false); return; }
      const savedIds = Object.keys(savedSnap.val());
      try {
        const listSnap = await get(ref(db, "listings"));
        if (listSnap.exists()) {
          const all = listSnap.val();
          const items = savedIds
            .map(id => all[id] ? { id, ...all[id] } : null)
            .filter(Boolean)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setListings(items);
        }
      } catch (e) { }
      setLoading(false);
    });
    return () => unsub();
  }, [currentUser]);

  async function unsave(id, e) {
    e.preventDefault();
    e.stopPropagation();
    await update(ref(db, `saved/${currentUser.uid}`), { [id]: null });
    setListings(prev => prev.filter(l => l.id !== id));
  }

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-white border-b px-4 py-4 sticky top-14 z-10 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-full hover:bg-gray-100">
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        <div>
          <h2 className="text-lg font-bold text-gray-800">Saved Items</h2>
          {listings.length > 0 && <p className="text-xs text-gray-400">{listings.length} saved listing{listings.length !== 1 ? "s" : ""}</p>}
        </div>
      </div>

      <div className="px-4 pt-4 max-w-lg mx-auto">
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[1,2,3,4].map(i => <div key={i} className="h-44 bg-white rounded-2xl animate-pulse" />)}
          </div>
        ) : listings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Heart size={52} className="text-gray-200 mb-4" />
            <p className="text-gray-600 font-semibold text-lg mb-1">No saved items yet</p>
            <p className="text-gray-400 text-sm mb-6">Tap the ❤️ on any listing to save it here.</p>
            <Link to="/browse" className="bg-blue-600 text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-blue-700 flex items-center gap-2">
              <Search size={16} /> Browse Listings
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {listings.map(l => {
              const isSold = l.status === "sold";
              return (
                <Link key={l.id} to={`/listing/${l.id}`} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition block relative">
                  <div className="h-36 bg-gray-100 overflow-hidden relative">
                    {l.imageUrl
                      ? <img src={l.imageUrl} alt={l.title} className={`w-full h-full object-cover ${isSold ? "opacity-50" : ""}`} />
                      : <div className="w-full h-full flex items-center justify-center"><BookOpen size={28} className="text-gray-200" /></div>}
                    {isSold && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="bg-red-600 text-white font-bold text-xs px-2 py-0.5 rounded-full rotate-[-15deg]">SOLD</span>
                      </div>
                    )}
                    <button
                      onClick={e => unsave(l.id, e)}
                      className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow"
                    >
                      <Heart size={14} className="text-red-500 fill-red-500" />
                    </button>
                  </div>
                  <div className="p-3">
                    <p className="font-semibold text-sm line-clamp-1 text-gray-800">{l.title}</p>
                    <p className={`font-bold text-sm mt-0.5 ${isSold ? "text-gray-400 line-through" : "text-blue-600"}`}>
                      {l.price === 0 ? "Negotiable" : `₹${l.price}`}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{l.college}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
